import { t } from 'i18next';
import { useCallback, useMemo } from 'react';
import { useCopyToClipboard } from 'usehooks-ts';

import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { isProductionEnv } from '~utils/isProductionEnv';

import buildLlmPrompt from './buildLlmPrompt';

interface UseLlmPromptArgs {
  apiBaseUrl: string;
  companyId: string;
  companyName: string;
}

export default function useLlmPrompt({
  apiBaseUrl,
  companyId,
  companyName,
}: UseLlmPromptArgs) {
  const { toasterStore } = useStores();
  const [, copyToClipboard] = useCopyToClipboard();
  const isProduction = isProductionEnv();

  const prompt = useMemo(
    () => buildLlmPrompt({ apiBaseUrl, companyId, companyName, isProduction }),
    [apiBaseUrl, companyId, companyName, isProduction],
  );

  const copyPrompt = useCallback(async () => {
    const didCopy = await copyToClipboard(prompt);

    toasterStore.push(
      didCopy
        ? alert(t('administration.api_clients.llm_prompt_copied'), AlertTypes.success)
        : alert(t('administration.api_clients.llm_prompt_copy_failed'), AlertTypes.error),
    );

    return didCopy;
  }, [prompt, copyToClipboard]);

  return { copyPrompt };
}
